const ANNEX_B_START_CODE = new Uint8Array([0x00, 0x00, 0x00, 0x01]);
export function concatBuffers(arrays) {
    const total = arrays.reduce((s, a) => s + a.byteLength, 0);
    const out = new Uint8Array(total);
    let pos = 0;
    for (const a of arrays) {
        const u8 = a instanceof Uint8Array ? a : new Uint8Array(a);
        out.set(u8, pos);
        pos += u8.byteLength;
    }
    return out.buffer;
}
/**
 * Convert length-prefixed NALUs (4-byte big-endian length headers)
 * to Annex-B format (0x00000001 start codes).
 */
export function lengthPrefixedToAnnexB(buf) {
    const view = new DataView(buf);
    const parts = [];
    let offset = 0;
    while (offset + 4 <= buf.byteLength) {
        const naluLen = view.getUint32(offset);
        offset += 4;
        if (naluLen === 0 || offset + naluLen > buf.byteLength)
            break;
        parts.push(ANNEX_B_START_CODE);
        parts.push(new Uint8Array(buf, offset, naluLen));
        offset += naluLen;
    }
    return concatBuffers(parts);
}
/**
 * Parse an avcC (AVC Decoder Configuration Record) into
 * individual SPS and PPS NALUs.
 */
export function extractAVCParams(desc) {
    if (!desc)
        return [];
    const buf = desc instanceof ArrayBuffer
        ? desc
        : desc.buffer.slice(desc.byteOffset, desc.byteOffset + desc.byteLength);
    const view = new DataView(buf);
    const nalus = [];
    let offset = 5;
    const numSPS = view.getUint8(offset) & 0x1f;
    offset++;
    for (let i = 0; i < numSPS; i++) {
        const len = view.getUint16(offset);
        offset += 2;
        nalus.push(new Uint8Array(buf, offset, len));
        offset += len;
    }
    const numPPS = view.getUint8(offset);
    offset++;
    for (let i = 0; i < numPPS; i++) {
        const len = view.getUint16(offset);
        offset += 2;
        nalus.push(new Uint8Array(buf, offset, len));
        offset += len;
    }
    return nalus;
}
/**
 * Parse an hvcC (HEVC Decoder Configuration Record) into
 * individual VPS, SPS, and PPS NALUs.
 */
export function extractHEVCParams(desc) {
    if (!desc)
        return [];
    const buf = desc instanceof ArrayBuffer
        ? desc
        : desc.buffer.slice(desc.byteOffset, desc.byteOffset + desc.byteLength);
    const view = new DataView(buf);
    const nalus = [];
    let offset = 22;
    const numArrays = view.getUint8(offset);
    offset++;
    for (let a = 0; a < numArrays; a++) {
        offset++; // array completeness + NALU type byte
        const numNalus = view.getUint16(offset);
        offset += 2;
        for (let n = 0; n < numNalus; n++) {
            const len = view.getUint16(offset);
            offset += 2;
            nalus.push(new Uint8Array(buf, offset, len));
            offset += len;
        }
    }
    return nalus;
}
/**
 * Prepend parameter NALUs (each preceded by a start code) to an Annex-B buffer.
 * Used to insert VPS/SPS/PPS before keyframes.
 */
export function prependNalus(annexbBuf, nalus) {
    const parts = [];
    for (const nalu of nalus) {
        parts.push(ANNEX_B_START_CODE);
        parts.push(nalu);
    }
    parts.push(annexbBuf instanceof Uint8Array ? annexbBuf : new Uint8Array(annexbBuf));
    return concatBuffers(parts);
}
/**
 * In-place AVCC→Annex-B conversion: 4-byte big-endian NAL length prefixes
 * occupy exactly the bytes of a 4-byte start code, so the conversion is
 * length-preserving — each prefix is overwritten with 00 00 00 01 and the
 * payload bytes never move. No allocations, no copies.
 *
 * The complete structure is validated BEFORE any byte is touched (a
 * partial rewrite would corrupt the frame): returns true when `data`
 * parsed exactly as length-prefixed NALUs and was rewritten in place;
 * false leaves the buffer untouched and the caller falls back to the
 * copying converter (or treats the data as already Annex-B).
 *
 * Only mutate buffers you own — e.g. the private per-frame views produced
 * by the SAB channel decode, which have a single consumer.
 */
export function lengthPrefixedToAnnexBInPlace(data) {
    const len = data.byteLength;
    if (len < 5)
        return false;
    let offset = 0;
    while (offset + 4 <= len) {
        const naluLen = ((data[offset] << 24) |
            (data[offset + 1] << 16) |
            (data[offset + 2] << 8) |
            data[offset + 3]) >>>
            0;
        if (naluLen === 0 || offset + 4 + naluLen > len)
            return false;
        offset += 4 + naluLen;
    }
    if (offset !== len)
        return false;
    offset = 0;
    while (offset < len) {
        const naluLen = ((data[offset] << 24) |
            (data[offset + 1] << 16) |
            (data[offset + 2] << 8) |
            data[offset + 3]) >>>
            0;
        data[offset] = 0;
        data[offset + 1] = 0;
        data[offset + 2] = 0;
        data[offset + 3] = 1;
        offset += 4 + naluLen;
    }
    return true;
}
/**
 * Tight standalone ArrayBuffer for APIs that transfer their input (e.g.
 * the RTP injector worker). A subarray view's `.buffer` must never be
 * transferred directly — it would detach the whole backing frame.
 */
export function toTransferableBuffer(b) {
    if (b instanceof ArrayBuffer)
        return b;
    return b.buffer.slice(b.byteOffset, b.byteOffset + b.byteLength);
}
/**
 * Rewrite an SDP m=video line to keep only the payload types
 * matching `codecName` (e.g. "H264", "H265").
 */
export function preferCodecInSDP(sdp, codecName) {
    const lines = sdp.split('\r\n');
    const codecPTs = new Set();
    const re = new RegExp(`^a=rtpmap:(\\d+)\\s+${codecName}\\/`, 'i');
    for (const line of lines) {
        const m = line.match(re);
        if (m)
            codecPTs.add(m[1]);
    }
    if (codecPTs.size === 0)
        return sdp;
    const keepPTs = new Set(codecPTs);
    for (const line of lines) {
        const m = line.match(/^a=fmtp:(\d+)\s+apt=(\d+)/);
        if (m && codecPTs.has(m[2]))
            keepPTs.add(m[1]);
    }
    return lines
        .map((line) => {
        if (line.startsWith('m=video')) {
            const p = line.split(' ');
            return [...p.slice(0, 3), ...keepPTs].join(' ');
        }
        return line;
    })
        .filter((line) => {
        const m = line.match(/^a=(rtpmap|fmtp|rtcp-fb):(\d+)\s/);
        if (m && !keepPTs.has(m[2]))
            return false;
        return true;
    })
        .join('\r\n');
}
/**
 * Add the playout-delay RTP header extension to SDP if not already present.
 * This tells the WebRTC stack to include min/max playout delay in RTP
 * packets, allowing the receiver to minimize its jitter buffer.
 */
export function addPlayoutDelayExtension(sdp) {
    const PLAYOUT_DELAY_URI = 'http://www.webrtc.org/experiments/rtp-hdrext/playout-delay';
    if (sdp.includes(PLAYOUT_DELAY_URI))
        return sdp;
    const usedIds = new Set();
    for (const m of sdp.matchAll(/a=extmap:(\d+)/g)) {
        usedIds.add(Number.parseInt(m[1], 10));
    }
    let extId = 1;
    while (usedIds.has(extId) && extId < 15)
        extId++;
    if (extId >= 15)
        return sdp;
    const lines = sdp.split('\r\n');
    const out = [];
    let added = false;
    for (const line of lines) {
        out.push(line);
        if (!added && line.startsWith('a=extmap:')) {
            added = true;
            out.push(`a=extmap:${extId} ${PLAYOUT_DELAY_URI}`);
        }
    }
    return out.join('\r\n');
}
/**
 * Inject `b=AS:100000` bandwidth lines into SDP to prevent
 * the loopback connection from being bandwidth-limited.
 */
export function setHighBandwidth(sdp) {
    const lines = sdp.split('\r\n').filter((l) => !l.startsWith('b='));
    const out = [];
    for (const line of lines) {
        out.push(line);
        if (line.startsWith('c=')) {
            out.push('b=AS:100000');
        }
    }
    return out.join('\r\n');
}
